<template>
  <div class="sponsored-ad-form">
    <CRow class="mb-3">
      <CCol lg="6" md="12" sm="12">
        <label>Banner Background Color</label>
        <div class="color-picker-container">
          <input
            type="color"
            :value="bannerBackgroundColorHex"
            @input="updateBannerBackgroundColor"
            class="color-picker-input"
          />
          <div class="hex-input-wrapper">
            <span class="hex-prefix">#</span>
            <input
              type="text"
              v-model="advert.metadata.banner_background_color"
              :is-valid="hexValidator"
              placeholder="F9DC5C"
              class="hex-input"
              maxlength="6"
              @input="validateAndUpdateBannerBg"
              @paste="handleBannerBgPaste"
              @keyup="validateAndUpdateBannerBg"
            />
          </div>
        </div>
        <small class="text-muted"
          >Default: F9DC5C | Copy/paste hex codes or use color picker</small
        >
      </CCol>
      <CCol lg="6" md="12" sm="12">
        <label>Banner Text Color</label>
        <div class="color-picker-container">
          <input
            type="color"
            :value="bannerTextColorHex"
            @input="updateBannerTextColor"
            class="color-picker-input"
          />
          <div class="hex-input-wrapper">
            <span class="hex-prefix">#</span>
            <input
              type="text"
              v-model="advert.metadata.banner_text_color"
              :is-valid="hexValidator"
              placeholder="000000"
              class="hex-input"
              maxlength="6"
              @input="validateAndUpdateBannerText"
              @paste="handleBannerTextPaste"
              @keyup="validateAndUpdateBannerText"
            />
          </div>
        </div>
        <small class="text-muted"
          >Default: 000000 | Copy/paste hex codes or use color picker</small
        >
      </CCol>
    </CRow>
    <CInput
      v-model="bannerDescription"
      label="Advertisement Description"
      :placeholder="defaultBannerDescription"
    />

    <landing-page-form :advertisement="advert" :show-logo-input="true" />

    <advertisement-media-form
      :advertisement="advert"
      :selected="selected"
      :show-logo-input="true"
      :show-existing-web-link="true"
      :show-logo-required="true"
      @show-display="showDisplay"
      @remove-thumbnail="removeThumbnail"
    />

    <language-form :advertisement="advert" :is-create="isCreate" />
  </div>
</template>
<script>
import LandingPageForm from './LandingPageForm.vue';
import LanguageForm from './LanguageForm.vue';
import AdvertisementMediaForm from './AdvertisementMediaForm.vue';
import {
  DEFAULT_BANNER_BACKGROUND_COLOR,
  DEFAULT_BANNER_TEXT_COLOR,
} from '@/constant/constants';

export default {
  name: 'SponsoredAdForm',
  components: { AdvertisementMediaForm, LanguageForm, LandingPageForm },
  props: {
    advertisement: {
      type: Object,
      required: true,
    },
    selected: {
      type: Boolean,
      required: true,
    },
    isCreate: {
      type: Boolean,
      required: false,
      default: true,
    },
    adv: {
      type: Object,
      required: false,
      default: () => ({ name: '' }),
    },
  },
  data() {
    return {};
  },
  mounted() {
    this.initializeSponsoredDefaults();
  },
  watch: {
    advertisement: {
      handler() {
        this.initializeSponsoredDefaults();
      },
      deep: true,
    },
  },
  computed: {
    advert() {
      return this.advertisement;
    },
    defaultBannerDescription() {
      const advertiserName = this.adv?.name || 'Advertiser';
      return `Brought to you by ${advertiserName}`;
    },
    bannerDescription: {
      get() {
        return this.advert.metadata.banner_description || '';
      },
      set(value) {
        const isEmptyOrUndefined =
          !value || value.trim() === '' || value.includes('undefined');
        const finalValue = isEmptyOrUndefined
          ? this.defaultBannerDescription
          : value;
        this.$set(this.advert.metadata, 'banner_description', finalValue);
      },
    },
    bannerBackgroundColorHex() {
      const color =
        this.advert.metadata.banner_background_color ||
        DEFAULT_BANNER_BACKGROUND_COLOR;
      return color.startsWith('#') ? color : `#${color}`;
    },
    bannerTextColorHex() {
      const color =
        this.advert.metadata.banner_text_color || DEFAULT_BANNER_TEXT_COLOR;
      return color.startsWith('#') ? color : `#${color}`;
    },
  },
  methods: {
    hexValidator(val) {
      if (!val || val?.length === 0) return true;
      const cleanVal = val.startsWith('#') ? val.substring(1) : val;
      return (
        typeof cleanVal === 'string' &&
        cleanVal.length === 6 &&
        /^[0-9A-Fa-f]{6}$/.test(cleanVal)
      );
    },
    updateBannerBackgroundColor(event) {
      const hexColor = event.target.value.substring(1); // Remove the # symbol
      this.advert.metadata.banner_background_color = hexColor;
    },
    updateBannerTextColor(event) {
      const hexColor = event.target.value.substring(1); // Remove the # symbol
      this.advert.metadata.banner_text_color = hexColor;
    },
    validateAndUpdateBannerBg(event) {
      const processedValue = this.processHexInput(event.target.value);
      this.advert.metadata.banner_background_color = processedValue;
      // Update the input value to the processed version
      this.$nextTick(() => {
        if (event.target.value !== processedValue) {
          event.target.value = processedValue;
        }
      });
    },
    validateAndUpdateBannerText(event) {
      const processedValue = this.processHexInput(event.target.value);
      this.advert.metadata.banner_text_color = processedValue;
      // Update the input value to the processed version
      this.$nextTick(() => {
        if (event.target.value !== processedValue) {
          event.target.value = processedValue;
        }
      });
    },
    processHexInput(value) {
      if (!value) return '';
      let cleanValue = value.startsWith('#') ? value.substring(1) : value;

      cleanValue = cleanValue.replace(/[^0-9A-Fa-f]/g, '');

      cleanValue = cleanValue.toUpperCase().substring(0, 6);

      return cleanValue;
    },
    handleBannerBgPaste(event) {
      event.preventDefault();
      const pastedText = (event.clipboardData || window.clipboardData).getData(
        'text'
      );
      const processedValue = this.processHexInput(pastedText);
      this.advert.metadata.banner_background_color = processedValue;
      event.target.value = processedValue;
    },
    handleBannerTextPaste(event) {
      event.preventDefault();
      const pastedText = (event.clipboardData || window.clipboardData).getData(
        'text'
      );
      const processedValue = this.processHexInput(pastedText);
      this.advert.metadata.banner_text_color = processedValue;
      event.target.value = processedValue;
    },
    showDisplay(value) {
      this.$emit('show-display', value);
    },
    removeThumbnail() {
      this.$emit('remove-thumbnail');
    },
    initializeSponsoredDefaults() {
      if (!this.advert.metadata) {
        this.$set(this.advert, 'metadata', {});
      }

      if (!this.advert.metadata.banner_background_color) {
        this.$set(
          this.advert.metadata,
          'banner_background_color',
          DEFAULT_BANNER_BACKGROUND_COLOR
        );
      }
      if (!this.advert.metadata.banner_text_color) {
        this.$set(
          this.advert.metadata,
          'banner_text_color',
          DEFAULT_BANNER_TEXT_COLOR
        );
      }

      if (
        !this.advert.metadata.banner_description ||
        this.advert.metadata.banner_description.includes('undefined')
      ) {
        this.$set(
          this.advert.metadata,
          'banner_description',
          this.defaultBannerDescription
        );
      }
    },
  },
};
</script>

<style scoped>
.color-picker-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 5px;
}

.color-picker-input {
  width: 50px;
  height: 35px;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
}

.hex-input-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
  background: white;
}

.hex-input-wrapper:focus-within {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.hex-prefix {
  padding: 8px 0 8px 12px;
  color: #666;
  font-family: monospace;
  font-weight: bold;
  background: #f8f9fa;
  user-select: none;
}

.hex-input {
  flex: 1;
  padding: 8px 12px 8px 4px;
  border: none;
  outline: none;
  font-family: monospace;
  text-transform: uppercase;
  background: transparent;
}

.hex-input::placeholder {
  color: #999;
  font-family: monospace;
}
</style>
